import SearchIcon from '@mui/icons-material/Search';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import InputAdornment from '@mui/material/InputAdornment';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import { useTheme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { collection, doc, getDoc, getDocs, query, updateDoc, where } from 'firebase/firestore';
import { useState } from 'react';
import { db } from '@/core/services/firebase';
import type { AdminRunner } from '@/core/types';

interface RunnerLinkModalProps {
  runner: AdminRunner | null;
  raceAlias: string;
  onClose: () => void;
  onLinked: () => void;
}

interface SearchResult {
  id: string;
  bib: string;
  fullName: string;
  email: string;
  birth: string;
}

/** 8文字のランダム文字列を生成 */
function generateTimingHash(): string {
  return Math.random().toString(36).slice(-8);
}

export function RunnerLinkModal({ runner, raceAlias, onClose, onLinked }: RunnerLinkModalProps) {
  const theme = useTheme();
  const adminGradient = theme.custom?.adminGradient || theme.custom?.gradient || theme.palette.primary.main;

  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [selectedRunner, setSelectedRunner] = useState<SearchResult | null>(null);
  const [searching, setSearching] = useState(false);
  const [linking, setLinking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!runner) return null;

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;

    setSearching(true);
    setError(null);
    setSelectedRunner(null);

    try {
      const runnersRef = collection(db, 'runners');
      // 姓で前方一致検索
      const q = query(
        runnersRef,
        where('raceAlias', '==', raceAlias),
        where('fullName', '>=', searchQuery),
        where('fullName', '<', `${searchQuery}\uf8ff`)
      );

      const snapshot = await getDocs(q);
      const results: SearchResult[] = [];

      for (const docSnapshot of snapshot.docs) {
        const data = docSnapshot.data();
        // 登録済みランナーのみ（emailがある）
        if (data.email) {
          results.push({
            id: docSnapshot.id,
            bib: data.bib,
            fullName: data.fullName,
            email: data.email,
            birth: data.birth,
          });
        }
      }

      setSearchResults(results);
    } catch (err) {
      console.error('Search failed:', err);
      setError('検索に失敗しました');
    } finally {
      setSearching(false);
    }
  };

  const handleLink = async () => {
    if (!selectedRunner || !runner) return;

    setLinking(true);
    setError(null);

    try {
      // ソースランナー（登録済み）からデータを取得
      const sourceRef = doc(db, 'runners', selectedRunner.id);
      const sourceSnapshot = await getDoc(sourceRef);

      if (!sourceSnapshot.exists()) {
        throw new Error('紐付け元のランナーが見つかりません');
      }

      const sourceData = sourceSnapshot.data();

      // ターゲットランナー（未登録）に登録情報をコピー + 新規timingHash生成
      const targetRef = doc(db, 'runners', runner.id);
      await updateDoc(targetRef, {
        fullName: sourceData.fullName,
        fullName_kana: sourceData.fullName_kana || null,
        fullName_katakana: sourceData.fullName_katakana || null,
        fullName_hiragana: sourceData.fullName_hiragana || null,
        email: sourceData.email,
        birth: sourceData.birth,
        timingHash: generateTimingHash(),
      });

      onLinked();
    } catch (err) {
      console.error('Link failed:', err);
      setError(err instanceof Error ? err.message : '紐付けに失敗しました');
      setLinking(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <Dialog open={!!runner} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>ランナー紐付け</DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* 紐付け対象 */}
        <Box sx={{ mb: 3, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            紐付け対象（未登録ランナー）
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Chip label={`ゼッケン: ${runner.bib}`} />
            {runner.tempFullName && (
              <Chip
                label={runner.tempFullName}
                sx={{
                  border: '2px solid transparent',
                  backgroundImage: `linear-gradient(#fff, #fff), ${adminGradient}`,
                  backgroundOrigin: 'border-box',
                  backgroundClip: 'padding-box, border-box',
                }}
              />
            )}
          </Box>
        </Box>

        {/* 検索 */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            登録済みランナーを検索（姓で検索）
          </Typography>
          <TextField
            fullWidth
            size="small"
            placeholder="例: 山田"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: searching && <CircularProgress size={20} />,
            }}
          />
          <Button
            variant="contained"
            size="small"
            onClick={handleSearch}
            disabled={searching}
            sx={{
              mt: 1,
              background: adminGradient,
              '&:hover': { opacity: 0.9 },
            }}
          >
            検索
          </Button>
        </Box>

        {/* 検索結果 */}
        {searchResults.length > 0 && (
          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              検索結果 ({searchResults.length}件)
            </Typography>
            <List dense sx={{ maxHeight: 200, overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1 }}>
              {searchResults.map((result) => (
                <ListItemButton
                  key={result.id}
                  selected={selectedRunner?.id === result.id}
                  onClick={() => setSelectedRunner(result)}
                >
                  <ListItemText primary={`${result.fullName} (ゼッケン: ${result.bib})`} secondary={result.email} />
                </ListItemButton>
              ))}
            </List>
          </Box>
        )}

        {searchResults.length === 0 && searchQuery && !searching && (
          <Typography variant="body2" color="text.secondary">
            該当するランナーが見つかりません
          </Typography>
        )}

        {/* 選択中のランナー */}
        {selectedRunner && (
          <Box sx={{ mt: 2, p: 2, bgcolor: 'success.light', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              選択中
            </Typography>
            <Typography variant="body2">
              {selectedRunner.fullName} (ゼッケン: {selectedRunner.bib})
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {selectedRunner.email}
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClose}
          disabled={linking}
          sx={{
            border: '2px solid transparent',
            backgroundImage: `linear-gradient(#fff, #fff), ${adminGradient}`,
            backgroundOrigin: 'border-box',
            backgroundClip: 'padding-box, border-box',
          }}
        >
          キャンセル
        </Button>
        <Button
          variant="contained"
          onClick={handleLink}
          disabled={!selectedRunner || linking}
          sx={{
            background: adminGradient,
            '&:hover': { opacity: 0.9 },
          }}
        >
          {linking ? <CircularProgress size={20} /> : '紐付け実行'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
